import { ArrowRight, Clock, HeartHandshake, Leaf, Mail, MapPin, type LucideIcon } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { A, Band, Head, arr, rv, str } from "./ui";
import { langCode } from "./theme";

/* 关于页专用的区块：原则、领导团队、组织所在地。
   和站内其它区块一样，所有事实(人名、职位、地址、邮箱)都由 site.json 提供，这里不编造，
   也不用生成的人像——领导团队用纯文字条目，克制、像真实的小型非营利组织。 */

const ICONS: Record<string, LucideIcon> = { leaf: Leaf, hands: HeartHandshake, clock: Clock };

/** 原则：三栏纯文字条目，顶部一条细线收住，配一枚描边小图标（不用 emoji、不用卡片）。 */
function Principles({ title, subtitle, items, tone }: SectionProps) {
  const list = arr(items).slice(0, 3);
  return (
    <Band id="principles" tone={tone}>
      <Head title={title} subtitle={subtitle} />
      <ul className="grid gap-x-10 gap-y-10 md:grid-cols-3 md:gap-x-12">
        {list.map((p, i) => {
          const Icon = ICONS[str(p.icon).toLowerCase()] || Leaf;
          return (
            <li key={i} {...rv(i)} className="border-t border-foreground/20 pt-6">
              <Icon className="size-6 text-primary" strokeWidth={1.5} aria-hidden />
              <h3 className="mt-5 text-xl md:text-2xl">{str(p.title)}</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">{str(p.description)}</p>
            </li>
          );
        })}
      </ul>
    </Band>
  );
}

/** 领导团队：左标题右名单，每条用一条分隔线，姓名 + 职位 + 一句话，没有任何头像或履历。 */
function Leadership({ title, subtitle, note, items, tone }: SectionProps) {
  const list = arr(items).slice(0, 5);
  return (
    <Band id="leadership" tone={tone}>
      <div className="grid gap-10 lg:grid-cols-[1fr_1.5fr] lg:gap-16">
        <div>
          <Head title={title} subtitle={subtitle} />
          {note ? <p className="-mt-6 text-sm leading-relaxed text-muted-foreground">{str(note)}</p> : null}
        </div>
        <ul className="divide-y divide-border border-y border-border">
          {list.map((m, i) => (
            <li key={i} {...rv(i)} className="grid gap-2 py-7 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] sm:gap-8">
              <div>
                <h3 className="text-xl md:text-2xl">{str(m.name)}</h3>
                <p className="mt-1.5 text-xs font-semibold uppercase tracking-[.14em] text-primary md:text-sm">{str(m.role)}</p>
              </div>
              <p className="leading-relaxed text-muted-foreground">{str(m.bio)}</p>
            </li>
          ))}
        </ul>
      </div>
    </Band>
  );
}

/** 所在地：地址 + 可点击的邮箱 + 一句正文 + 去联系表单的文字链接，右边是地址对应的地图。 */
function LocationSection({ title, subtitle, address, email, contactLabel, contactHref, tone }: SectionProps) {
  const { lang } = usePage();
  const hl = langCode(lang);
  const lines = str(address)
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const mail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str(email)) ? str(email) : "";
  const query = lines.join(", ");
  const embed = query ? `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=15&hl=${hl}&output=embed` : "";
  const mapHref = query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}&hl=${hl}` : "";
  return (
    <Band id="location" tone={tone}>
      <div className="grid gap-10 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
        <div className="flex flex-col">
          <Head title={title} subtitle={subtitle} />
          <div className="flex flex-col gap-6">
            {lines.length > 0 ? (
              <div className="flex items-start gap-4">
                <span className="mt-0.5 grid size-10 flex-none place-items-center border border-border text-primary">
                  <MapPin className="size-5" aria-hidden />
                </span>
                <address className="not-italic leading-relaxed">
                  {lines.map((l) => (
                    <span key={l} className="block">
                      {l}
                    </span>
                  ))}
                  {mapHref ? (
                    <a href={mapHref} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                      View on Google Maps
                      <ArrowRight className="size-3.5" aria-hidden />
                    </a>
                  ) : null}
                </address>
              </div>
            ) : null}
            {mail ? (
              <div className="flex items-center gap-4">
                <span className="grid size-10 flex-none place-items-center border border-border text-primary">
                  <Mail className="size-5" aria-hidden />
                </span>
                <a href={`mailto:${mail}`} className="font-semibold break-all hover:text-primary hover:underline">
                  {mail}
                </a>
              </div>
            ) : null}
          </div>
          {contactLabel ? (
            <A
              to={str(contactHref) || "/#contact"}
              className="mt-8 inline-flex w-fit items-center gap-2 border-b border-primary/40 pb-1 font-semibold text-primary transition hover:border-primary"
            >
              {str(contactLabel)}
              <ArrowRight className="size-4" aria-hidden />
            </A>
          ) : null}
        </div>
        <div {...rv(1)} className="relative min-h-80 overflow-hidden border border-border bg-muted shadow-card lg:min-h-[420px]">
          {embed ? (
            <iframe title={query} src={embed} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 size-full border-0" />
          ) : (
            <div className="absolute inset-0 grid place-items-center text-muted-foreground">
              <MapPin className="size-12" aria-hidden />
            </div>
          )}
        </div>
      </div>
    </Band>
  );
}

export const ABOUT_RENDERERS = {
  principles: Principles,
  leadership: Leadership,
  orgLocation: LocationSection,
};

/** 底色交替名单：和 sections.tsx 的 ALTERNATING 合并使用 */
export const ABOUT_ALTERNATING = ["principles", "leadership", "orgLocation"];